import ApiService from '@/common/api.service'
import { displayNotification } from '@/common/notification'

export const state = {
  dwellings: [],
  livingOptions: [],
  packages: [],
  properties: [],
  structuredProperties: [],
  subsections: [],
  isPushing: false,
  isPushed: false,
}

export const getters = {
  livingOptions (state) { return state.livingOptions },
  subsections (state) { return state.subsections },
  availableProperties (state) {
    return state.properties.filter(property => {
      return property.status === 'Available'
    })
  },
  structuredProperties (state) { return state.structuredProperties },
  properties (state) { return state.properties },
  dwellings (state) { return state.dwellings },
  packages (state) { return state.packages },
  isPushing (state) { return state.isPushing },
  isPushed (state) { return state.isPushed },
}

export const mutations = {
  ADD_DWELLING (state, dwelling) {
    state.dwellings.unshift(dwelling)
  },

  SET_DWELLINGS (state, dwellings) {
    state.dwellings = dwellings
  },

  ADD_PROPERTY (state, property) {
    state.properties.unshift(property)
  },

  SET_PROPERTIES (state, properties) {
    state.properties = properties
  },

  SET_STRUCTURED_PROPERTIES (state, properties) {
    state.structuredProperties = properties
  },

  ADD_SUBSECTION (state, subsection) {
    state.subsections.unshift(subsection)
  },

  SET_SUBSECTIONS (state, subsections) {
    state.subsections = subsections
  },

  ADD_LIVING_OPTION (state, livingOption) {
    state.livingOptions.unshift(livingOption)
  },
  
  SET_LIVING_OPTIONS (state, livingOptions) {
    state.livingOptions = livingOptions
  },

  ADD_PACKAGE (state, _package) {
    state.packages.unshift(_package)
  },
  
  SET_PACKAGES (state, packages) {
    state.packages = packages
  },
}

export const actions = {
  async PUSH_TO_PROD ({ state }) {
    try {
      state.isPushing = true
      const { data } = await ApiService.put('nexus/update/touch', {
        git_pull: false,
        npm_install: false,
        run_build: false,
        restart_app:false,
        run_release:false,
        crm_release:true
      })
      state.isPushing = false
      state.isPushed = true
    } catch (e) {
      displayNotification('Error pushing to prod: ' + e, 'warning')
    }
  },

  async FETCH_LIVING_OPTIONS ({ commit }) {
    try {
      const { data } = await ApiService.get('living-options')
      commit('SET_LIVING_OPTIONS', data)
    } catch (e) {
      displayNotification('Error Fetching Living Options: ' + e, 'warning')
    }
  },

  async ADD_NEW_LIVING_OPTION ({ commit }) {
    try {
      const { data } = await ApiService.get('living-options/schema')
      commit('ADD_LIVING_OPTION', data)
      return data
    } catch (e) {
      displayNotification('Error Adding New Living Option: ' + e, 'warning')
    }
  },  

  async SAVE_LIVING_OPTION ({ commit, dispatch }, livingOption) {
    try {
      await ApiService.put(`living-options/${livingOption._id}`, { livingOption: livingOption })
      displayNotification("Successfully updated living option", "success");
    } catch (e) {
      displayNotification('Error Saving Living Option: ' + e, 'warning')
    } finally {
      dispatch('FETCH_LIVING_OPTIONS')
    }
  },

  async DELETE_LIVING_OPTION ({ commit, dispatch }, livingOptionId) {
    try {
      await ApiService.delete('living-options', livingOptionId)
      displayNotification("Successfully deleted living option", "success");
    } catch (e) {
      displayNotification('Error Deleting Living Option: ' + e, 'warning')
    } finally {
      dispatch('FETCH_LIVING_OPTIONS')
    }    
  },
  
  async CLONE_LIVING_OPTION ({ commit }, livingOption) {
    try {
      const { data } = await ApiService.get('living-options/schema')
      delete livingOption._id
      livingOption.label = '__COPY__' + livingOption.label
      Object.assign(data, livingOption)
      commit('ADD_LIVING_OPTION', data)
      return data
    } catch (e) {
      displayNotification('Error Cloning Living Option: ' + e, 'warning')
    }
  },

  async FETCH_SUBSECTIONS ({ commit }) {
    try {
      const { data } = await ApiService.get('subsections')
      commit('SET_SUBSECTIONS', data)
    } catch (e) {
      displayNotification('Error Fetching Subsections: ' + e, 'warning')
    }
  },

  async ADD_NEW_SUBSECTION ({ commit }) {
    try {
      const { data } = await ApiService.get('subsections/schema')
      commit('ADD_SUBSECTION', data)
      return data
    } catch (e) {
      displayNotification('Error adding new subsection: ' + e, 'warning')
    }
  },   

  async SAVE_SUBSECTION ({ dispatch }, subsection) {
    if (subsection.properties) delete subsection.properties

    try {
      await ApiService.put(`subsections/${subsection._id}`, { subsection: subsection })
      displayNotification("Successfully updated subsection", "success");
    } catch (e) {
      displayNotification('Error Saving Subsection: ' + e, 'warning')
    } finally {
      dispatch('FETCH_SUBSECTIONS')
    }
  },

  async DELETE_SUBSECTION ({ dispatch }, subsectionId) {
    try {
      await ApiService.delete('subsections', subsectionId)
      displayNotification("Deleted subsection", "success")
    } catch (e) {
      displayNotification('Error Deleting Subsection: ' + e, 'warning')
    } finally {
      dispatch('FETCH_SUBSECTIONS')
    }
  },

  async CLONE_SUBSECTION ({ commit }, subsection) {
    try {
      const { data } = await ApiService.get('subsections/schema')
      delete subsection._id
      subsection.label = '__COPY__' + subsection.label
      Object.assign(data, subsection)
      commit('ADD_SUBSECTION', data)
      return data
    } catch (e) {
      displayNotification('Error Cloning Subsection: ' + e, 'warning')
    }
  },

  async FETCH_PROPERTIES ({ commit }) {
    try {
      const { data } = await ApiService.get('properties')
      commit('SET_PROPERTIES', data)
    } catch (e) {
      displayNotification('Error Fetching Properties: ' + e, 'warning')
    }
  },

  async FETCH_STRUCTURED_PROPERTIES ({ commit }) {
    try {
      const { data } = await ApiService.get('properties/structured')
      commit('SET_STRUCTURED_PROPERTIES', data)
    } catch (e) {
      displayNotification('Error Fetching Properties: ' + e, 'warning')
    }
  },  

  async ADD_NEW_PROPERTY ({ commit }) {
    try {
      const { data } = await ApiService.get('properties/schema')
      commit('ADD_PROPERTY', data)
      return data
    } catch (e) {
      displayNotification('Error Adding New Property: ' + e, 'warning')
    }
  },

  async SAVE_PROPERTY ({ commit, dispatch }, property) {
    try {
      await ApiService.put(`properties/${property._id}`, { property: property })
      displayNotification("Successfully saved property", "success");
    } catch (e) {
      displayNotification('Error saving property: ' + e, 'warning')
    } finally {
      dispatch('FETCH_PROPERTIES')
    }
  },

  async SAVE_PROPERTIES ({ commit, dispatch }, properties) {
    try {
      await ApiService.post('properties', { properties: properties })
      displayNotification(`Successfully updated ${properties.length} properties`, "success");
    } catch (e) {
      displayNotification('Error saving properties: ' + e, 'warning')
    } finally {
      dispatch('FETCH_PROPERTIES')
    }
  },

  async DELETE_PROPERTY ({ commit, dispatch }, propertyId) {
    try {
      await ApiService.delete('properties', propertyId)
      displayNotification("Deleted property", "success")
    } catch (e) {
      displayNotification('Error deleting property: ' + e, 'warning')
    } finally {
      dispatch('FETCH_PROPERTIES')
    }
  },    

  async CLONE_PROPERTY ({ commit }, property) {
    try {
      const { data } = await ApiService.get('properties/schema')
      delete property._id
      property.label = '__COPY__' + property.label
      Object.assign(data, property)
      commit('ADD_PROPERTY', data)
      return data
    } catch (e) {
      displayNotification('Error Cloning Property: ' + e, 'warning')
    }
  },  

  async FETCH_DWELLINGS ({ commit }) {
    try {
      const { data } = await ApiService.get('dwellings')
      commit('SET_DWELLINGS', data)
    } catch (e) {
      displayNotification('Error Fetching Dwellings: ' + e, 'warning')
    }
  },

  async ADD_NEW_DWELLING ({ commit }) {
    try {
      const { data } = await ApiService.get('dwellings/schema')
      commit('ADD_DWELLING', data)
      return data
    } catch (e) {
      displayNotification('Error Adding New Property: ' + e, 'warning')
    }
  },  

  async SAVE_DWELLING ({ dispatch }, dwelling) {
    try {
      await ApiService.put(`dwellings/${dwelling._id}`, { dwelling: dwelling })
      displayNotification("Successfully saved dwelling", "success");
    } catch (e) {
      displayNotification('Error saving dwelling: ' + e, 'warning')
    } finally {
      dispatch('FETCH_DWELLINGS')
    }
  },

  async DELETE_DWELLING ({ dispatch }, dwellingId) {
    try {
      await ApiService.delete('dwellings', dwellingId)
      displayNotification("Deleted dwelling", "success");
    } catch (e) {
      displayNotification('Error deleting dwelling: ' + e, 'warning')
    } finally {
      dispatch('FETCH_DWELLINGS')
    }
  },

  async CLONE_DWELLING ({ commit }, dwelling) {
    try {
      const { data } = await ApiService.get('dwellings/schema')
      delete dwelling._id
      dwelling.label = '__COPY__' + dwelling.label
      Object.assign(data, dwelling)
      commit('ADD_DWELLING', data)
      return data
    } catch (e) {
      displayNotification('Error cloning dwelling: ' + e, 'warning')
    }
  },    

  async FETCH_PACKAGES ({ commit }) {
    try {
      const { data } = await ApiService.get('packages')
      commit('SET_PACKAGES', data)
    } catch (e) {
      displayNotification('Error fetching packages: ' + e, 'warning')
    }
  },

  async ADD_NEW_PACKAGE ({ commit }) {
    try {
      const { data } = await ApiService.get('packages/schema')
      commit('ADD_PACKAGE', data)
      return data
    } catch (e) {
      displayNotification('Error adding new package: ' + e, 'warning')
    }
  },   

  async CREATE_PACKAGE ({ dispatch }, _package) {
    try {
      await ApiService.put(`packages/${_package._id}`, { package: _package })
      displayNotification("Successfully created package", "success");
    } catch (e) {
      displayNotification('Error creating packages: ' + e, 'warning')
    } finally {
      dispatch('FETCH_PACKAGES')
    }
  },

  async SAVE_PACKAGE ({ dispatch }, _package) {
    if (!_package.propertyId || !_package.dwellingId) {
      displayNotification('Property and dwelling required', 'warning')
    } else {
      try {
        await ApiService.put(`packages/${_package._id}`, { package: _package })
        displayNotification("Successfully saved package", "success");
      } catch (e) {
        displayNotification('Error saving packages: ' + e, 'warning')
      } finally {
        dispatch('FETCH_PACKAGES')
      }
    }
  },

  async DELETE_PACKAGE ({ dispatch }, packageId) {
    try {
      await ApiService.delete('packages', packageId)
      displayNotification("Deleted package", "success");
    } catch (e) {
      displayNotification('Error deleting package: ' + e, 'warning')
    } finally {
      dispatch('FETCH_PACKAGES')
    }
  },

  async CLONE_PACKAGE ({ commit }, _package) {
    try {
      const { data } = await ApiService.get('packages/schema')
      delete _package._id
      _package.label = '__COPY__' + _package.label
      Object.assign(data, _package)
      commit('ADD_PACKAGE', data)
      return data
    } catch (e) {
      displayNotification('Error cloning package: ' + e, 'warning')
    }
  },      
}



export default {
  state,
  actions,
  mutations,
  getters
}